import type { Handedness, PinchCalibration } from '../domain/types'

// Learns each hand's own open and closed pinch distances while the player
// plays, so hand size and camera distance stop mattering. The estimates chase
// the widest and narrowest distances seen over the last few seconds: quickly
// when a hand proves it can go further, slowly when it never gets as far as the
// current estimate assumed.

export interface PinchAdaptOptions {
  openMedian?: number
  closedMedian?: number
  windowSize?: number
  minimumGap?: number
}

export class PinchAdapter {
  private openEstimate: number
  private closedEstimate: number
  private readonly window: number[] = []
  private readonly windowSize: number
  private readonly minimumGap: number
  private readonly initialOpen: number
  private readonly initialClosed: number

  constructor(private readonly handedness: Handedness, options: PinchAdaptOptions = {}) {
    this.initialOpen = options.openMedian ?? 1.05
    this.initialClosed = options.closedMedian ?? 0.2
    this.openEstimate = this.initialOpen
    this.closedEstimate = this.initialClosed
    this.windowSize = Math.max(10, options.windowSize ?? 90)
    this.minimumGap = options.minimumGap ?? 0.25
  }

  // Feed every observed thumb–index distance (relative to palm width).
  observe(distanceRatio: number): void {
    if (!Number.isFinite(distanceRatio) || distanceRatio <= 0) return
    this.window.push(distanceRatio)
    if (this.window.length > this.windowSize) this.window.shift()
    let widest = 0
    let narrowest = Number.POSITIVE_INFINITY
    for (const value of this.window) { widest = Math.max(widest, value); narrowest = Math.min(narrowest, value) }
    this.openEstimate += (widest - this.openEstimate) * (widest > this.openEstimate ? 0.2 : 0.02)
    this.closedEstimate += (narrowest - this.closedEstimate) * (narrowest < this.closedEstimate ? 0.2 : 0.004)
    this.openEstimate = Math.max(this.openEstimate, this.closedEstimate + this.minimumGap)
    this.closedEstimate = Math.max(0.02, Math.min(this.closedEstimate, this.openEstimate - this.minimumGap))
  }

  // Threshold fractions reproduce the shipped defaults (contact 0.38, release
  // 0.62 for an open 1.05 / closed 0.2 hand), so the first adaptation is a no-op.
  calibration(): PinchCalibration {
    const gap = this.openEstimate - this.closedEstimate
    return {
      handedness: this.handedness,
      openMedian: this.openEstimate,
      closedMedian: this.closedEstimate,
      contactThreshold: this.closedEstimate + gap * 0.21,
      releaseThreshold: this.closedEstimate + gap * 0.5,
      valid: true,
      calibratedAt: 0
    }
  }

  reset(): void {
    this.openEstimate = this.initialOpen
    this.closedEstimate = this.initialClosed
    this.window.length = 0
  }
}

// True when the thresholds moved enough that the detector should be updated.
export function calibrationDiffers(a: PinchCalibration, b: PinchCalibration, tolerance = 0.03): boolean {
  return Math.abs(a.contactThreshold - b.contactThreshold) > tolerance || Math.abs(a.releaseThreshold - b.releaseThreshold) > tolerance
}
